import { Control, Controller, type FieldValues } from "react-hook-form";

import {
  Grid,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { theme } from "@covertree/theme";
import FormRadioOption from "./FormRadioOption";
import { Tooltip } from "../../content/Tooltip";

export interface RadioOptionType {
  title: string;
  value: unknown;
  disabled?: boolean;
  image?: string;
  tooltip?: string;
  helpText?: string;
  hidden?: boolean;
  subtitle?: string;
}

export type Props<TValues extends FieldValues> = {
  items: RadioOptionType[];
  label: string;
  loading?: boolean;
  selected: unknown;
  name: string;
  control: Control<TValues>;
  tooltip?: string;
  onOptionSelect?: (v: unknown) => void;
  direction?: "row" | "column";
  variant?: "default" | "blank";
  disabled?: boolean;
  fontSize?: number;
  labelFontSize?: number;
};

export const FormRadioGroup = <TValues extends FieldValues>({
  label,
  items = [],
  loading,
  selected,
  control,
  name,
  tooltip,
  onOptionSelect = () => {},
  direction = "row",
  variant = "default",
  disabled = false,
  fontSize,
  labelFontSize,
}: Props<TValues>) => {
  const labelFontSizeValue = labelFontSize ?? fontSize;

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      render={({ field: { onChange, ...field } }) => (
        <FormControl fullWidth>
          <FormLabel
            sx={{
              display: "flex",
              alignItems: "center",
              ...(labelFontSizeValue && {
                fontSize: `${labelFontSizeValue}px`,
              }),
            }}>
            {label}
            {tooltip && (
              <Tooltip
                dataTestId={`${label}-tooltip`}
                tooltip={tooltip ?? ""}
                size="large"
                sx={{
                  paddingBottom: theme.spacing(0.5),
                  marginLeft: theme.spacing(1),
                  backgroundColor: theme.palette.common.white,
                }}
                placement="bottom"
              />
            )}
          </FormLabel>
          <RadioGroup {...field}>
            <Grid
              container
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              spacing={direction === "row" ? 1 : 3}>
              {items
                .filter((item) => !item.hidden)
                .map((item) => (
                  <Grid key={item.title} item xs={direction === "row" ? 6 : 12}>
                    <FormControlLabel
                      id={`${item.title
                        .toLowerCase()
                        .split(" ")
                        .join("-")}-radio-group-item`}
                      control={<Radio sx={{ display: "none" }} />}
                      value={item.value}
                      disabled={loading || disabled || item?.disabled}
                      sx={{
                        marginLeft: 0,
                        marginRight: direction === "row" ? 1 : 0,
                        "& .MuiFormControlLabel-label": {
                          marginLeft: 0,
                          width: "100%",
                        },
                        "& .MuiFormControlLabel-root": {
                          marginRight: 0,
                        },
                        "& > .MuiIconButton-root": {
                          display: "none",
                        },
                      }}
                      label={
                        <FormRadioOption
                          {...item}
                          disabled={loading || disabled || item.disabled}
                          selected={selected === item.value}
                          direction={direction}
                          variant={variant}
                          fontSize={fontSize}
                          onClick={() => {
                            onChange(item.value);
                            onOptionSelect(item.value);
                          }}
                        />
                      }
                    />
                  </Grid>
                ))}
            </Grid>
          </RadioGroup>
        </FormControl>
      )}
    />
  );
};

export default FormRadioGroup;
